import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { HOME_SAMPLE_REPORT_ID } from "./homeSample";
import {
  newSubmissionId,
  readUtmParams,
  savePartialLead,
  submitUseCaseForm,
} from "../../use-cases/submit";
import {
  FREE_MAIL_HINT,
  isFreeMailEmail,
} from "../../use-cases/shared/free-mail";
import { BookCallLink } from "./BookCallLink";
import { CORE_FIELDS } from "../../use-cases/shared/form-fields";
import type {
  FormField,
  SelectFormField,
  UseCaseConfig,
} from "../../use-cases/types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Homepage capture form. Uses the same core schema and submit helper as the
 * use-case landing pages, with `use_case: 'general'`. The wizard maps onto
 * the five core fields: Email → Name (first + last together) → Audience
 * Size + YIR program, followed by a final step of any extra (non-core)
 * questions appended to `cta.fields` below. Once the email step is done the
 * lead is saved as `partial`, so drop-offs are still followable.
 *
 * To add or change home-page questions, edit the non-core entries in
 * `GENERAL_CONFIG.cta.fields` — exactly like a use-case vertical. Their
 * answers ride along in the submission's `vertical_answers` blob.
 */
const GENERAL_CONFIG: UseCaseConfig = {
  slug: "general",
  menuLabel: "",
  navLabel: "",
  teaser: "",
  meta: { title: "", description: "", ogImage: "" },
  hero: {
    eyebrowLabel: "",
    subhead: "",
    headlineLead: "",
    headlineEmphasis: "",
    body: "",
    byline: "",
  },
  narrativeSample: {
    // Same narrative as the hero, so the auto-reply email's inline sample
    // link matches what the visitor saw on the page before submitting.
    reportId: HOME_SAMPLE_REPORT_ID,
    badgeLabel: "",
    sampleTitle: "",
    eyebrow: "",
    captionHeadline: "",
    captionBody: "",
    disclosure: "",
    fullScreenLabel: "",
    ctaLabel: "",
  },
  scale: {
    eyebrow: "",
    leadIn: "",
    firstHighlight: "",
    middle: "",
    secondHighlight: "",
    tail: "",
    subtitle: "",
    counters: [],
  },
  pillars: { eyebrow: "", headline: "", points: [] },
  cta: {
    sectionEyebrow: "",
    headlineLead: "",
    headlineEmphasis: "",
    headlineTail: "",
    body: "",
    submitLabel: "",
    sendingLabel: "",
    continueLabel: "",
    success: { headline: "", body: "" },
    fields: [
      ...CORE_FIELDS,
      {
        key: "industry",
        type: "text",
        label: "What industry are you in?",
        placeholder: "e.g. Healthcare, SaaS, Higher Ed",
        required: true,
        core: false,
      },
      {
        key: "audience_feeling",
        type: "textarea",
        label: "What do you want your audience to feel?",
        placeholder: "e.g. Proud of the year. Confident about what comes next.",
        required: true,
        core: false,
      },
    ],
  },
};

// Pull select fields by key so labels/options track the shared schema.
const fieldByKey = Object.fromEntries(CORE_FIELDS.map((f) => [f.key, f]));
const AUDIENCE_SIZE = fieldByKey.audience_size as SelectFormField;
const CURRENT_YIR = fieldByKey.current_yir_program as SelectFormField;

// Extra (non-core) questions appended to the wizard. Derived from the config
// so adding/removing a question there is all it takes to change the form.
const EXTRA_FIELDS = GENERAL_CONFIG.cta.fields.filter((f) => !f.core);
const CORE_STEPS = 3;
const TOTAL_STEPS = CORE_STEPS + (EXTRA_FIELDS.length > 0 ? 1 : 0);
const INPUT_CLASS =
  "w-full px-4 py-3 bg-fwd-soft border-[1.5px] border-fwd-ink/10 rounded text-fwd-ink placeholder:text-fwd-mid/50 focus:border-fwd-red focus:outline-none transition-colors";

export const CTAForm = () => {
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [audienceSize, setAudienceSize] = useState("");
  const [currentYir, setCurrentYir] = useState("");
  const [extraValues, setExtraValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(EXTRA_FIELDS.map((f) => [f.key, ""])),
  );
  const [honeypot, setHoneypot] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [utm, setUtm] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const hasAdvanced = useRef(false);
  // Ties the step-1 partial save to the final submit (sheet upsert key).
  const submissionId = useRef(newSubmissionId());
  const partialSaved = useRef(false);

  useEffect(() => {
    if (step === 1 && !hasAdvanced.current) return;
    hasAdvanced.current = true;
    // Auto-focus first input after step transition renders
    const timer = setTimeout(() => {
      const el = formRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([tabindex="-1"]), select, textarea',
      );
      el?.focus();
    }, 350); // after framer-motion transition
    return () => clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    setUtm(readUtmParams(window.location.search));
  }, []);

  const validateStep = (current: number): string | null => {
    if (current === 1 && !EMAIL_REGEX.test(workEmail.trim())) {
      return "Please enter a valid email address.";
    }
    if (current === 2) {
      if (!firstName.trim() || !lastName.trim()) {
        return "Please enter your first and last name.";
      }
    }
    if (current === 3) {
      if (!audienceSize) return "Please select a range.";
      if (!currentYir) return "Please pick the option that fits best.";
    }
    if (current === 4) {
      for (const field of EXTRA_FIELDS) {
        const value = (extraValues[field.key] ?? "").trim();
        if (field.required && !value) {
          return `Please fill in “${field.label}”.`;
        }
        if (field.type === "textarea" && field.required && value.length < 3) {
          return `Please add a little more detail to “${field.label}”.`;
        }
      }
    }
    return null;
  };

  const handleAdvance = async () => {
    const stepError = validateStep(step);
    if (stepError) {
      setError(stepError);
      return;
    }
    setError("");

    if (step === 1 && !partialSaved.current && !honeypot.trim()) {
      partialSaved.current = true;
      savePartialLead({
        config: GENERAL_CONFIG,
        workEmail: workEmail.trim(),
        utm,
        submissionId: submissionId.current,
      });
    }

    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      return;
    }

    if (honeypot.trim()) {
      setIsSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitUseCaseForm({
        config: GENERAL_CONFIG,
        values: {
          first_name: firstName,
          last_name: lastName,
          work_email: workEmail,
          audience_size: audienceSize,
          current_yir_program: currentYir,
          ...extraValues,
        },
        utm,
        submissionId: submissionId.current,
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      setError(
        "We couldn’t submit your request. Please check your connection and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFinalStep = step === TOTAL_STEPS;

  return (
    <section
      id="get-started"
      className="scroll-mt-(--site-header-h,4rem) bg-fwd-yellow py-24 md:py-32 px-8 md:px-16 lg:px-20"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <Reveal>
          <div>
            <span className="text-fwd-ink/45 text-xs font-bold tracking-[0.2em] uppercase block mb-5">
              Get Started
            </span>
            <h2 className="font-display text-[clamp(2.2rem,4vw,3.5rem)] font-normal leading-tight text-fwd-ink mb-5">
              See what a personalized narrative could look like for your
              customers.
            </h2>
            <p className="text-fwd-ink/55 text-lg leading-relaxed font-light max-w-sm">
              Tell us a little. We&rsquo;ll send back a mockup within a couple
              of days.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="bg-white rounded-lg p-10 shadow-[0_8px_40px_rgba(17,17,24,0.1)]">
            <AnimatePresence mode="wait">
              {!isSubmitted ? (
                <motion.form
                  ref={formRef}
                  key={`step-${step}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleAdvance();
                  }}
                >
                  {step === 1 && (
                    <>
                      <label
                        htmlFor="cta-work-email"
                        className="text-sm font-semibold text-fwd-ink tracking-wide"
                      >
                        Work email
                      </label>
                      <input
                        id="cta-work-email"
                        type="email"
                        value={workEmail}
                        onChange={(e) => setWorkEmail(e.target.value)}
                        placeholder="you@company.com"
                        aria-describedby={
                          isFreeMailEmail(workEmail)
                            ? "cta-work-email-hint"
                            : undefined
                        }
                        className={INPUT_CLASS}
                      />
                      {isFreeMailEmail(workEmail) && (
                        <p
                          id="cta-work-email-hint"
                          className="text-fwd-mid text-sm"
                        >
                          {FREE_MAIL_HINT}
                        </p>
                      )}
                    </>
                  )}

                  {step === 2 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="cta-first-name"
                          className="text-sm font-semibold text-fwd-ink tracking-wide"
                        >
                          First name
                        </label>
                        <input
                          id="cta-first-name"
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Sam"
                          className={INPUT_CLASS}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="cta-last-name"
                          className="text-sm font-semibold text-fwd-ink tracking-wide"
                        >
                          Last name
                        </label>
                        <input
                          id="cta-last-name"
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Reyes"
                          className={INPUT_CLASS}
                        />
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="cta-audience-size"
                          className="text-sm font-semibold text-fwd-ink tracking-wide"
                        >
                          {AUDIENCE_SIZE.label}
                        </label>
                        <div className="relative">
                          <select
                            id="cta-audience-size"
                            value={audienceSize}
                            onChange={(e) => setAudienceSize(e.target.value)}
                            className={`${INPUT_CLASS} appearance-none cursor-pointer pr-10`}
                          >
                            <option value="" disabled>
                              Select a range
                            </option>
                            {AUDIENCE_SIZE.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-fwd-mid text-xs pointer-events-none">
                            &#9662;
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="cta-current-yir"
                          className="text-sm font-semibold text-fwd-ink tracking-wide"
                        >
                          {CURRENT_YIR.label}
                        </label>
                        <div className="relative">
                          <select
                            id="cta-current-yir"
                            value={currentYir}
                            onChange={(e) => setCurrentYir(e.target.value)}
                            className={`${INPUT_CLASS} appearance-none cursor-pointer pr-10`}
                          >
                            <option value="" disabled>
                              Select an option
                            </option>
                            {CURRENT_YIR.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-fwd-mid text-xs pointer-events-none">
                            &#9662;
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="flex flex-col gap-4">
                      {EXTRA_FIELDS.map((field) => (
                        <ExtraFieldInput
                          key={field.key}
                          field={field}
                          value={extraValues[field.key] ?? ""}
                          onChange={(v) =>
                            setExtraValues((prev) => ({
                              ...prev,
                              [field.key]: v,
                            }))
                          }
                        />
                      ))}
                    </div>
                  )}

                  {/* Honeypot — visually hidden, ignored by humans. */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    name="company_website"
                    className="absolute -left-[10000px] w-px h-px opacity-0 pointer-events-none"
                  />

                  {error && <p className="text-fwd-red text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`self-start px-7 py-3 rounded text-sm font-semibold tracking-wider uppercase transition-opacity disabled:opacity-50 ${
                      isFinalStep
                        ? "bg-fwd-red text-white hover:opacity-90"
                        : "bg-fwd-ink text-white hover:opacity-85"
                    }`}
                  >
                    {isSubmitting
                      ? "Sending…"
                      : isFinalStep
                        ? "Send it →"
                        : "Continue →"}
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-4"
                >
                  <div className="text-fwd-red text-3xl mb-4">{"✦"}</div>
                  <h3 className="font-display text-2xl text-fwd-ink mb-3">
                    We&rsquo;ll be in touch.
                  </h3>
                  <p className="text-fwd-mid text-[0.95rem] leading-relaxed">
                    Someone from our team will reach out within one to two
                    business days with a look at what a personalized Year in
                    Review could look like for your audience.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {!isSubmitted && (
              <div className="flex gap-2 mt-6">
                {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
                  const dot = i + 1;
                  return (
                    <div
                      key={dot}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        dot === step ? "bg-fwd-red" : "bg-fwd-ink/10"
                      }`}
                    />
                  );
                })}
              </div>
            )}
          </div>
          {!isSubmitted && <BookCallLink tone="light" />}
        </Reveal>
      </div>
    </section>
  );
};

/**
 * Renderer for the home form's extra (non-core) questions, styled to match
 * the home page card. Handles text/email, textarea, and select fields so new
 * questions of any type can be added to `GENERAL_CONFIG.cta.fields`.
 */
const ExtraFieldInput = ({
  field,
  value,
  onChange,
}: {
  field: FormField;
  value: string;
  onChange: (v: string) => void;
}) => {
  const id = `cta-${field.key}`;
  const label = (
    <label
      htmlFor={id}
      className="text-sm font-semibold text-fwd-ink tracking-wide"
    >
      {field.label}
    </label>
  );

  if (field.type === "select") {
    return (
      <div className="flex flex-col gap-2">
        {label}
        <div className="relative">
          <select
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${INPUT_CLASS} appearance-none cursor-pointer pr-10`}
          >
            <option value="" disabled>
              Select an option
            </option>
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-fwd-mid text-xs pointer-events-none">
            &#9662;
          </span>
        </div>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="flex flex-col gap-2">
        {label}
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={`${INPUT_CLASS} min-h-[110px] resize-y leading-relaxed`}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {label}
      <input
        id={id}
        type={field.type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={INPUT_CLASS}
      />
    </div>
  );
};
